import { Injectable } from '@angular/core';
import { AsyncSubject, Observable, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { switchMap } from 'rxjs/operators';
import { AxwayTokenFetcherService, TOKEN_SERVICES_PARAM } from '@ptl/xhr';

export interface LatLng {
  lat: number;
  lng: number;
}

@Injectable({
  providedIn: 'root'
})

export class GoogleMapApiService {

  constructor(private axwayTokenFetcherService: AxwayTokenFetcherService) { }

  getLatLngFromFormattedZipCode(zipCode: string): Observable<LatLng> {
    return this.getGeocoderRef()
      .pipe(
        switchMap(Geocoder => {
          const geocodeSubject = new AsyncSubject<LatLng>();
          if ((/(^\d{5}$)|(^\d{5}-\d{4}$)/).test(zipCode)) {
            Geocoder.geocode({ address: `${zipCode}+US` }, (results, status) => {
              if (status === google.maps.GeocoderStatus.OK) {
                const latLngResult: LatLng = {
                  lat: results[0].geometry.location.lat(),
                  lng: results[0].geometry.location.lng()
                };
                geocodeSubject.next(latLngResult);
                geocodeSubject.complete();
              } else {
                geocodeSubject.error(status);
              }
            });
          } else {
            geocodeSubject.error('invalid zipcode');
          }
          return geocodeSubject.asObservable();
        })
      );
  }

  getGeocoderRef(): Observable<google.maps.Geocoder> {

    if (this.isMapsScriptLoaded()) {
      return of(new google.maps.Geocoder());
    } else {
      const subject = new AsyncSubject<google.maps.Geocoder>();
      const script = this.loadMapsScripts(subject);
      document.getElementsByTagName('head')[0].appendChild(script);
      return subject.asObservable();
    }
  }

  private loadMapsScripts(subject: AsyncSubject<google.maps.Geocoder>): HTMLScriptElement {

    const channelId = '10'; // these Channels may become specific to product/pages.
    const script: HTMLScriptElement = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;

    // set error handler early
    script.onerror = (error: any) => subject.error('script failed to load');

    // Fetch API key asynchronously and then set src and handlers.
    this.axwayTokenFetcherService.fetchTokenFromGCP('ALL', TOKEN_SERVICES_PARAM.GOOGLE_API_KEY)
      .then(response => {
        const key = response && response.body && response.body.googleApiKey ? response.body.googleApiKey : '';
        script.src = 'https://maps.googleapis.com/maps/api/js?channel=' + channelId + '&key=' + key;

        if ((script as any).readyState) {
          (script as any).onreadystatechange = () => {
            if ((script as any).readyState === 'loaded' || (script as any).readyState === 'complete') {
              (script as any).onreadystatechange = null;
              subject.next(new google.maps.Geocoder());
              subject.complete();
            }
          };
        } else {
          script.onload = () => {
            subject.next(new google.maps.Geocoder());
            subject.complete();
          };
        }
      })
      .catch(err => subject.error('script failed to load'));

    return script;
  }

  isMapsScriptLoaded(): boolean {
    return !!document.querySelector('[src^=\'https://maps.googleapis.com/maps/api/js\']');
  }
}
